import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  BoltIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800',
  pending_payment: 'bg-amber-100 text-amber-800',
  past_due: 'bg-orange-100 text-orange-800',
  expired: 'bg-gray-100 text-gray-700',
  canceled: 'bg-red-100 text-red-800',
};

const STATUS_LABELS = {
  active: 'Active',
  pending_payment: 'Awaiting payment',
  past_due: 'Past due',
  expired: 'Expired',
  canceled: 'Canceled',
};

const COMPONENT_LABELS = {
  site: 'Site',
  crm: 'CRM',
  newsletter: 'News',
  omni: 'Omni',
};

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return '—';
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  return `${currency === 'ILS' ? '₪' : '$'}${value.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const SORT_COLUMNS = [
  { key: 'account_name', label: 'Account' },
  { key: 'tier', label: 'Tier' },
  { key: 'status', label: 'Status' },
  { key: 'components', label: 'Components' },
  { key: 'credits', label: 'Credits' },
  { key: 'price', label: 'Price' },
  { key: 'period_end', label: 'Period ends' },
  { key: 'updated_at', label: 'Modified' },
];

function SortHeader({ label, column, sortBy, sortOrder, onSort }) {
  const active = sortBy === column;
  return (
    <th
      aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-800"
      >
        {label}
        {active && sortOrder === 'desc' ? (
          <ChevronDownIcon className="w-3.5 h-3.5" />
        ) : (
          <ChevronUpIcon className={`w-3.5 h-3.5 ${active ? '' : 'text-gray-300'}`} />
        )}
      </button>
    </th>
  );
}

/**
 * Every account on the Engagement Bundle model, with the credit burn for the
 * current period so the team can spot an account about to run dry.
 */
const Bundles = () => {
  const navigate = useNavigate();
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [bundles, setBundles] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const loadBundles = useCallback(async () => {
    const params = new URLSearchParams();
    params.set('limit', '200');
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder);
    if (statusFilter) params.set('status', statusFilter);
    if (tierFilter) params.set('tier', tierFilter);
    if (search) params.set('search', search);

    const [listRes, catalogRes] = await Promise.all([
      axios.get(`${API_BASE_URL}/api/admin/bundles?${params}`),
      axios.get(`${API_BASE_URL}/api/admin/bundles/tiers`),
    ]);
    setBundles(listRes.data.bundles || []);
    setTiers(catalogRes.data.tiers || []);
    setAvailable(true);
  }, [statusFilter, tierFilter, search, sortBy, sortOrder]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await loadBundles();
    } catch (error) {
      if (error.response?.status === 404) {
        setAvailable(false);
      } else {
        console.error('Error loading bundles:', error);
        toast.error('Failed to load bundles');
      }
    } finally {
      setLoading(false);
    }
  }, [loadBundles]);

  useEffect(() => { load(); }, [load]);

  if (!available) {
    return (
      <AdminLayout>
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <SparklesIcon className="h-10 w-10 text-gray-300 mx-auto" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900">
            Engagement Bundle is not enabled
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Set <code className="px-1 bg-gray-100 rounded">FEATURE_ENGAGEMENT_BUNDLE=true</code> on
            the API to sell bundles from the backoffice.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Engagement Bundles</h1>
          <p className="text-sm text-gray-500 mt-1">
            Pre-paid, sales-activated accounts and their credit usage.
            Open an account to change its plan.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap gap-3">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search account name…"
          className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">All tiers</option>
          {tiers.map((t) => (
            <option key={t.key} value={t.key}>{t.displayName}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : bundles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No bundles yet. Sell one from an account&rsquo;s detail page.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {SORT_COLUMNS.map((column) => (
                  <SortHeader
                    key={column.key}
                    label={column.label}
                    column={column.key}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                  />
                ))}
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bundles.map((b) => {
                const included = Number(b.included_credits) || 0;
                const used = Number(b.used_credits) || 0;
                const pct = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
                const components = Array.isArray(b.components) ? b.components : [];

                const detailPath = `/bundles/${b.account_id}`;

                return (
                  <tr
                    key={b.account_id}
                    onClick={() => navigate(detailPath)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={detailPath}
                        onClick={(e) => e.stopPropagation()}
                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        {b.account_name || b.account_id}
                      </Link>
                      <div className="text-xs text-gray-500">
                        {b.website_count} site{b.website_count === 1 ? '' : 's'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 capitalize">{b.tier}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_STYLES[b.status] || STATUS_STYLES.expired}`}>
                        {STATUS_LABELS[b.status] || b.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {components.map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">
                            {COMPONENT_LABELS[c] || c}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <BoltIcon className={`h-4 w-4 ${pct >= 90 ? 'text-red-500' : 'text-gray-400'}`} />
                        <div>
                          <div className="text-sm text-gray-900">{used} / {included}</div>
                          {Number(b.topup_credits) > 0 && (
                            <div className="text-xs text-gray-500">+{b.topup_credits} top-up</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatMoney(b.amount, b.currency)}
                      <span className="text-xs text-gray-400"> /{b.billing_period === 'yearly' ? 'yr' : 'mo'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(b.period_end)}
                      {b.cancel_at_period_end && (
                        <div className="text-xs text-red-600">Cancels</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateTime(b.updated_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={detailPath}
                        onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-primary-600 inline-flex"
                        aria-label={`Open bundle for ${b.account_name || b.account_id}`}
                      >
                        <ChevronRightIcon className="w-5 h-5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
};

export default Bundles;
